/* eslint-disable react/no-danger */
import * as React from 'react';
import moment from 'moment-timezone';
import { getTheme, ITheme, mergeStyleSets } from '@fluentui/react/lib/Styling';
import { formatMoney, formatPercent } from 'utils/formatter';
import { formStyles } from 'styles/formStyles';
import classnames from 'classnames';
import { uniq } from 'lodash';
import {
    betreuerByNrSelector,
    lookupsLoadingFinishedSelector,
    produktgruppenByNrSelector,
} from 'store/lookups/selector';
import { useSelector } from 'react-redux';
import { LoadingIndicator } from 'layout/LoadingIndicator/LoadingIndicator';
import { authSelector } from 'store/auth/selector';
import { AuftragSummaryProps, useAuftragSummary } from './hooks/useAuftragSummary';
import { AuftragSummary, defaultLineData, FilterValues, LineData } from './types';
import { FilterInfo } from './FilterInfo';

const theme: ITheme = getTheme();
const { palette } = theme;

const odd = palette.neutralLight;
const even = palette.neutralLighterAlt;

export const styles = mergeStyleSets({
    container: {
        width: '100%',
        // height: 'calc(100vh - 120px)',
        overflowY: 'auto',
    },
    printContainer: {
        width: '100%',
        height: '100%',
        fontSize: '12px',
    },
    tdHead: {
        fontWeight: 700,
        paddingLeft: 2,
        paddingRight: 2,
        // eslint-disable-next-line no-useless-computed-key
        ['-webkit-print-color-adjust']: 'exact',
    },
    tdBody: {
        paddingLeft: 2,
        paddingRight: 2,
        borderBottom: '0.5px solid #CCC',
        // eslint-disable-next-line no-useless-computed-key
        ['-webkit-print-color-adjust']: 'exact',
    },

    borderTop: {
        borderTop: '1px solid',
    },
    strongBorderTop: {
        borderTop: '3px solid',
    },
    borderBottom: {
        borderBottom: '1px solid',
    },
    strongBorderBottom: {
        borderBottom: '3px solid',
    },
    noPadding: {
        paddingTop: 0,
        paddingBottom: 0,
    },
    growth: {
        fontSize: 12,
        lineHeight: 12,
    },
    positiveGrowth: {
        color: palette.green,
    },
    negativeGrowth: {
        color: palette.red,
    },
    auNr: {},
    ekNetto: {
        backgroundColor: odd,
    },
    vkNetto: {
        backgroundColor: even,
    },
    bMarge: {
        backgroundColor: odd,
    },
    tippProv: {
        backgroundColor: even,
    },
    skonto: {
        backgroundColor: odd,
    },
    fracht: {
        backgroundColor: even,
    },
    nMarge: {
        backgroundColor: odd,
    },
    bAnteil: {
        backgroundColor: even,
    },
    fAnteil: {
        backgroundColor: odd,
    },
    fAnteilTotal: {
        backgroundColor: odd,
        fontWeight: 700,
    },
    borderRight: {
        borderRight: '1px solid',
    },
    produktgruppen: {
        marginTop: 20,
    },
});

interface AuftragsuebersichtProps extends FilterValues {
    printView?: boolean;
}

export const Auftragsuebersicht: React.FC<AuftragsuebersichtProps> = (filterValues) => {
    const lookupsLoadingFinished = useSelector(lookupsLoadingFinishedSelector);
    const auth = useSelector(authSelector);
    const betreuerByNr = useSelector(betreuerByNrSelector);
    const produktgruppenByNr = useSelector(produktgruppenByNrSelector);

    const {
        datumVon,
        datumBis,
        printView,
        previousYear1,
        previousYear2,
        previousYear3,
        showBetreuer,
        showTelBetreuer,
    } = filterValues;

    const auftragSummaryPropsJ0 = React.useMemo<AuftragSummaryProps>(
        () => ({
            ...filterValues,
            enabled: true,
        }),
        [filterValues],
    );

    const auftragSummaryJ0 = useAuftragSummary(auftragSummaryPropsJ0);

    const auftragSummaryPropsJ1 = React.useMemo<AuftragSummaryProps>(
        () => ({
            ...filterValues,
            datumVon: moment(datumVon).subtract({ years: 1 }).format('YYYY-MM-DD'),
            datumBis: moment(datumBis).subtract({ years: 1 }).format('YYYY-MM-DD'),
            enabled: true,
        }),
        [datumVon, datumBis, filterValues],
    );

    const auftragSummaryJ1 = useAuftragSummary(auftragSummaryPropsJ1);

    const auftragSummaryPropsJ2 = React.useMemo<AuftragSummaryProps>(
        () => ({
            ...filterValues,
            datumVon: moment(datumVon).subtract({ years: 2 }).format('YYYY-MM-DD'),
            datumBis: moment(datumBis).subtract({ years: 2 }).format('YYYY-MM-DD'),
            enabled: true,
        }),
        [datumVon, datumBis, filterValues],
    );

    const auftragSummaryJ2 = useAuftragSummary(auftragSummaryPropsJ2);

    const auftragSummaryPropsJ3 = React.useMemo<AuftragSummaryProps>(
        () => ({
            ...filterValues,
            datumVon: moment(datumVon).subtract({ years: 3 }).format('YYYY-MM-DD'),
            datumBis: moment(datumBis).subtract({ years: 3 }).format('YYYY-MM-DD'),
            enabled: true,
        }),
        [datumVon, datumBis, filterValues],
    );

    const getAvg = (values: number[]): number => {
        let sum = 0;
        values.forEach((value) => {
            sum += value;
        });
        return Math.round((sum * 100) / values.length) / 100;
    };

    const auftragSummaryJ3 = useAuftragSummary(auftragSummaryPropsJ3);

    const auftragSummaryAvg = React.useMemo<AuftragSummary>(() => {
        const usedProduktgruppen: Record<string, number> = {};

        const totalAvg: LineData = { ...defaultLineData };
        const betreuerTotalAvg: {
            [key: number]: LineData;
        } = {};
        const telBetreuerTotalAvg: {
            [key: number]: LineData;
        } = {};

        if (auftragSummaryJ1.loading || auftragSummaryJ2.loading || auftragSummaryJ3.loading) {
            return {
                loading: true,
                count: 0,
                total: totalAvg,
                betreuerTotal: betreuerTotalAvg,
                telBetreuerTotal: telBetreuerTotalAvg,
                auftragLineDataList: [],
                fAnteilTotal: 0,
            };
        }

        const auftragSummaries = [auftragSummaryJ1, auftragSummaryJ2, auftragSummaryJ3];

        const countAvg = getAvg(auftragSummaries.map((s) => s.count));
        totalAvg.count = getAvg(auftragSummaries.map((s) => s.total.count));
        totalAvg.ekNetto = getAvg(auftragSummaries.map((s) => s.total.ekNetto));
        totalAvg.vkNetto = getAvg(auftragSummaries.map((s) => s.total.vkNetto));
        totalAvg.bMarge = getAvg(auftragSummaries.map((s) => s.total.bMarge));
        totalAvg.tippProv = getAvg(auftragSummaries.map((s) => s.total.tippProv));
        totalAvg.skonto = getAvg(auftragSummaries.map((s) => s.total.skonto));
        totalAvg.fracht = getAvg(auftragSummaries.map((s) => s.total.fracht));
        totalAvg.nMarge = getAvg(auftragSummaries.map((s) => s.total.nMarge));
        totalAvg.bAnteil = getAvg(auftragSummaries.map((s) => s.total.bAnteil));
        totalAvg.fAnteil = getAvg(auftragSummaries.map((s) => s.total.fAnteil));
        totalAvg.betrUm = getAvg(auftragSummaries.map((s) => s.total.betrUm));

        const fAnteilTotalAvg = getAvg(auftragSummaries.map((s) => s.fAnteilTotal));

        const betreuerNrList: number[] = [];
        const telBetreuerNrList: number[] = [];

        auftragSummaries.forEach((auftragSummary) => {
            Object.values(auftragSummary.betreuerTotal).forEach((betreuerLineData) => {
                betreuerNrList.push(betreuerLineData.betreuerNr);
            });
            Object.values(auftragSummary.telBetreuerTotal).forEach((betreuerLineData) => {
                telBetreuerNrList.push(betreuerLineData.betreuerNr);
            });

            if (auftragSummary.usedProduktgruppen) {
                Object.keys(auftragSummary.usedProduktgruppen).forEach((produktGruppeNr) => {
                    if (!usedProduktgruppen[produktGruppeNr]) {
                        usedProduktgruppen[produktGruppeNr] = 0;
                    }
                    usedProduktgruppen[produktGruppeNr] += auftragSummary.usedProduktgruppen?.[produktGruppeNr] || 0;
                });
            }
        });

        uniq(betreuerNrList).forEach((betreuerNr) => {
            betreuerTotalAvg[betreuerNr] = {
                ...defaultLineData,
                betreuerNr,
                name: betreuerByNr[betreuerNr]?.name || betreuerNr.toString(),
                count: getAvg(auftragSummaries.map((s) => s.betreuerTotal[betreuerNr]?.count || 0)),
                ekNetto: getAvg(auftragSummaries.map((s) => s.betreuerTotal[betreuerNr]?.ekNetto || 0)),
                vkNetto: getAvg(auftragSummaries.map((s) => s.betreuerTotal[betreuerNr]?.vkNetto || 0)),
                bMarge: getAvg(auftragSummaries.map((s) => s.betreuerTotal[betreuerNr]?.bMarge || 0)),
                tippProv: getAvg(auftragSummaries.map((s) => s.betreuerTotal[betreuerNr]?.tippProv || 0)),
                skonto: getAvg(auftragSummaries.map((s) => s.betreuerTotal[betreuerNr]?.skonto || 0)),
                fracht: getAvg(auftragSummaries.map((s) => s.betreuerTotal[betreuerNr]?.fracht || 0)),
                nMarge: getAvg(auftragSummaries.map((s) => s.betreuerTotal[betreuerNr]?.nMarge || 0)),
                bAnteil: getAvg(auftragSummaries.map((s) => s.betreuerTotal[betreuerNr]?.bAnteil || 0)),
                fAnteil: getAvg(auftragSummaries.map((s) => s.betreuerTotal[betreuerNr]?.fAnteil || 0)),
                betrUm: getAvg(auftragSummaries.map((s) => s.betreuerTotal[betreuerNr]?.betrUm || 0)),
            };
        });

        uniq(telBetreuerNrList).forEach((betreuerNr) => {
            telBetreuerTotalAvg[betreuerNr] = {
                ...defaultLineData,
                betreuerNr,
                name: betreuerByNr[betreuerNr]?.name || betreuerNr.toString(),
                count: getAvg(auftragSummaries.map((s) => s.telBetreuerTotal[betreuerNr]?.count || 0)),
                ekNetto: getAvg(auftragSummaries.map((s) => s.telBetreuerTotal[betreuerNr]?.ekNetto || 0)),
                vkNetto: getAvg(auftragSummaries.map((s) => s.telBetreuerTotal[betreuerNr]?.vkNetto || 0)),
                bMarge: getAvg(auftragSummaries.map((s) => s.telBetreuerTotal[betreuerNr]?.bMarge || 0)),
                tippProv: getAvg(auftragSummaries.map((s) => s.telBetreuerTotal[betreuerNr]?.tippProv || 0)),
                skonto: getAvg(auftragSummaries.map((s) => s.telBetreuerTotal[betreuerNr]?.skonto || 0)),
                fracht: getAvg(auftragSummaries.map((s) => s.telBetreuerTotal[betreuerNr]?.fracht || 0)),
                nMarge: getAvg(auftragSummaries.map((s) => s.telBetreuerTotal[betreuerNr]?.nMarge || 0)),
                bAnteil: getAvg(auftragSummaries.map((s) => s.telBetreuerTotal[betreuerNr]?.bAnteil || 0)),
                fAnteil: getAvg(auftragSummaries.map((s) => s.telBetreuerTotal[betreuerNr]?.fAnteil || 0)),
                betrUm: getAvg(auftragSummaries.map((s) => s.telBetreuerTotal[betreuerNr]?.betrUm || 0)),
            };
        });

        return {
            loading: false,
            count: countAvg,
            total: totalAvg,
            betreuerTotal: betreuerTotalAvg,
            telBetreuerTotal: telBetreuerTotalAvg,
            auftragLineDataList: [],
            fAnteilTotal: fAnteilTotalAvg,
            usedProduktgruppen,
        };
    }, [auftragSummaryJ1, auftragSummaryJ2, auftragSummaryJ3, betreuerByNr]);

    const renderHeaderRow = () => {
        const moneyClassName = classnames(
            styles.tdHead,
            formStyles.dataValueRight,
            printView ? formStyles.reportMoneyPrintCell : formStyles.reportMoneyCell,
        );

        const percentClassName = classnames(
            styles.tdHead,
            formStyles.dataValueRight,
            printView ? formStyles.reportPercentPrintCell : formStyles.reportPercentCell,
        );

        return (
            <tr>
                <td className={classnames(styles.tdHead)} />
                <td className={classnames(percentClassName, styles.ekNetto)} />
                <td className={classnames(moneyClassName, styles.ekNetto)}>EK Netto</td>
                <td className={classnames(percentClassName, styles.vkNetto)} />
                <td className={classnames(moneyClassName, styles.vkNetto)}>VK Netto</td>
                <td className={classnames(percentClassName, styles.bMarge)} />
                <td className={classnames(moneyClassName, styles.bMarge, styles.borderRight)}>B-Marge</td>
                <td className={classnames(percentClassName, styles.tippProv)} />
                <td className={classnames(moneyClassName, styles.tippProv)}>Tipp-Pr.</td>
                <td className={classnames(percentClassName, styles.skonto)} />
                <td className={classnames(moneyClassName, styles.skonto)}>Skonto</td>
                <td className={classnames(percentClassName, styles.fracht)} />
                <td className={classnames(moneyClassName, styles.fracht)}>Fracht</td>
                <td className={classnames(percentClassName, styles.nMarge)} />
                <td className={classnames(moneyClassName, styles.nMarge, styles.borderRight)}>N-Marge</td>
                <td className={classnames(percentClassName, styles.bAnteil)} />
                <td className={classnames(moneyClassName, styles.bAnteil)}>B-Anteil</td>
                {auth.rolle !== 'AD' && (
                    <>
                        <td className={classnames(percentClassName, styles.fAnteil)} />
                        <td className={classnames(moneyClassName, styles.fAnteil)}>F-Anteil</td>
                    </>
                )}
            </tr>
        );
    };

    const moneyClassName = classnames(
        styles.tdBody,
        formStyles.dataValueRight,
        printView ? formStyles.reportMoneyPrintCell : formStyles.reportMoneyCell,
    );
    const percentClassName = classnames(
        styles.tdBody,
        formStyles.dataValueRight,
        printView ? formStyles.reportPercentPrintCell : formStyles.reportPercentCell,
    );

    const renderMoneyWithGrowth = (
        value: number,
        valuePrev: number | undefined,
        valueAvg: number | undefined,
        className: string,
        borderRight?: boolean,
    ) => {
        let growthPrev: number | undefined = valuePrev ? (value * 100) / valuePrev - 100 : undefined;
        let colorPrev = '';

        if (growthPrev) {
            if (Math.abs(growthPrev) > 1000) {
                growthPrev = undefined;
            }

            if (growthPrev) {
                if (growthPrev > 0) {
                    colorPrev = styles.positiveGrowth;
                } else if (growthPrev < 0) {
                    colorPrev = styles.negativeGrowth;
                }
            }
        }

        let growthAvg: number | undefined = valueAvg ? (value * 100) / valueAvg - 100 : undefined;
        let colorAvg = '';

        if (growthAvg) {
            if (Math.abs(growthAvg) > 1000) {
                growthAvg = undefined;
            }

            if (growthAvg) {
                if (growthAvg > 0) {
                    colorAvg = styles.positiveGrowth;
                } else if (growthAvg < 0) {
                    colorAvg = styles.negativeGrowth;
                }
            }
        }

        return (
            <>
                <td className={classnames(percentClassName, className)}>
                    {growthPrev === undefined ? (
                        <span className={styles.growth}>&nbsp;</span>
                    ) : (
                        <span
                            className={classnames(styles.growth, colorPrev)}
                            title={
                                valuePrev
                                    ? `Vorjahr: ${formatMoney(valuePrev)}  Differenz: ${formatMoney(value - valuePrev)}`
                                    : ''
                            }
                        >
                            {formatPercent(growthPrev, 0)}
                        </span>
                    )}
                    <br />
                    {growthAvg === undefined ? (
                        <span className={styles.growth}>&nbsp;</span>
                    ) : (
                        <span
                            className={classnames(styles.growth, colorAvg)}
                            title={
                                valueAvg
                                    ? `Durchschnitt 3J: ${formatMoney(valueAvg)}  Differenz: ${formatMoney(
                                          value - valueAvg,
                                      )}`
                                    : ''
                            }
                        >
                            {formatPercent(growthAvg, 0)}
                        </span>
                    )}
                </td>
                <td
                    className={classnames(moneyClassName, className, {
                        [styles.borderRight]: borderRight,
                    })}
                >
                    {formatMoney(value)}
                </td>
            </>
        );
    };

    const renderLine = (title: string, line: LineData, lineCompare?: LineData, lineCompareAvg?: LineData) => {
        return (
            <tr key={title + line.auNr}>
                <td className={styles.tdBody}>{title}</td>
                {renderMoneyWithGrowth(line.ekNetto, lineCompare?.ekNetto, lineCompareAvg?.ekNetto, styles.ekNetto)}
                {renderMoneyWithGrowth(line.vkNetto, lineCompare?.vkNetto, lineCompareAvg?.vkNetto, styles.vkNetto)}
                {renderMoneyWithGrowth(line.bMarge, lineCompare?.bMarge, lineCompareAvg?.bMarge, styles.bMarge, true)}
                {renderMoneyWithGrowth(line.tippProv, lineCompare?.tippProv, lineCompareAvg?.tippProv, styles.tippProv)}
                {renderMoneyWithGrowth(line.skonto, lineCompare?.skonto, lineCompareAvg?.skonto, styles.skonto)}
                {renderMoneyWithGrowth(line.fracht, lineCompare?.fracht, lineCompareAvg?.fracht, styles.fracht)}
                {renderMoneyWithGrowth(line.nMarge, lineCompare?.nMarge, lineCompareAvg?.nMarge, styles.nMarge, true)}
                {renderMoneyWithGrowth(line.bAnteil, lineCompare?.bAnteil, lineCompareAvg?.bAnteil, styles.bAnteil)}
                {auth.rolle !== 'AD' &&
                    renderMoneyWithGrowth(line.fAnteil, lineCompare?.fAnteil, lineCompareAvg?.fAnteil, styles.fAnteil)}
            </tr>
        );
    };

    const renderYear = (
        title: string,
        filterValues2: AuftragSummaryProps | null,
        auftragSummary: AuftragSummary,
        auftragSummaryCompare: AuftragSummary | null,
        position: number,
    ) => {
        const className = position === 0 ? styles.strongBorderTop : '';

        return (
            <>
                <tr>
                    <td className={classnames(styles.tdBody, className)}>
                        <strong>
                            {title}
                            {filterValues2 && (
                                <>
                                    {moment(filterValues2.datumVon).format('DD.MM.YYYY')}
                                    {' - '}
                                    {moment(filterValues2.datumBis).format('DD.MM.YYYY')}
                                </>
                            )}
                        </strong>
                    </td>
                    <td className={classnames(percentClassName, className, styles.ekNetto)} />
                    <td className={classnames(percentClassName, className, styles.ekNetto)} />
                    <td className={classnames(percentClassName, className, styles.vkNetto)} />
                    <td className={classnames(percentClassName, className, styles.vkNetto)} />
                    <td className={classnames(percentClassName, className, styles.bMarge)} />
                    <td className={classnames(percentClassName, className, styles.bMarge, styles.borderRight)} />
                    <td className={classnames(percentClassName, className, styles.tippProv)} />
                    <td className={classnames(percentClassName, className, styles.tippProv)} />
                    <td className={classnames(percentClassName, className, styles.skonto)} />
                    <td className={classnames(percentClassName, className, styles.skonto)} />
                    <td className={classnames(percentClassName, className, styles.fracht)} />
                    <td className={classnames(percentClassName, className, styles.fracht)} />
                    <td className={classnames(percentClassName, className, styles.nMarge)} />
                    <td className={classnames(percentClassName, className, styles.nMarge, styles.borderRight)} />
                    <td className={classnames(percentClassName, className, styles.bAnteil)} />
                    <td className={classnames(percentClassName, className, styles.bAnteil)} />
                    {auth.rolle !== 'AD' && (
                        <>
                            <td className={classnames(percentClassName, className, styles.fAnteil)} />
                            <td className={classnames(percentClassName, className, styles.fAnteil)} />
                        </>
                    )}
                </tr>
                {renderLine(
                    'Gesamt (' + (auftragSummary ? auftragSummary.count : '...') + ')',
                    auftragSummary.total,
                    auftragSummaryCompare?.total,
                    auftragSummaryAvg?.total,
                )}
                <tr>
                    <td className={styles.tdBody}>%-Anteil</td>
                    <td className={classnames(percentClassName, styles.ekNetto)} />
                    <td className={classnames(percentClassName, styles.ekNetto)} />
                    <td className={classnames(percentClassName, styles.vkNetto)} />
                    <td className={classnames(percentClassName, styles.vkNetto)} />

                    {auftragSummary.total.vkNetto ? (
                        <>
                            <td className={classnames(percentClassName, styles.bMarge)} />
                            <td className={classnames(moneyClassName, styles.bMarge, styles.borderRight)}>
                                {formatPercent(auftragSummary.total.bMarge / (auftragSummary.total.vkNetto / 100), 2)}
                            </td>
                            <td className={classnames(percentClassName, styles.tippProv)} />
                            <td className={classnames(moneyClassName, styles.tippProv)}>
                                {formatPercent(auftragSummary.total.tippProv / (auftragSummary.total.vkNetto / 100), 2)}
                            </td>
                            <td className={classnames(percentClassName, styles.skonto)} />
                            <td className={classnames(moneyClassName, styles.skonto)}>
                                {formatPercent(auftragSummary.total.skonto / (auftragSummary.total.vkNetto / 100), 2)}
                            </td>
                            <td className={classnames(percentClassName, styles.fracht)} />
                            <td className={classnames(moneyClassName, styles.fracht)}>
                                {formatPercent(auftragSummary.total.fracht / (auftragSummary.total.vkNetto / 100), 2)}
                            </td>
                            <td className={classnames(percentClassName, styles.nMarge)} />
                            <td className={classnames(moneyClassName, styles.nMarge, styles.borderRight)}>
                                {formatPercent(auftragSummary.total.nMarge / (auftragSummary.total.vkNetto / 100), 2)}
                            </td>
                            <td className={classnames(percentClassName, styles.bAnteil)} />
                            <td className={classnames(moneyClassName, styles.bAnteil)}>
                                {formatPercent(auftragSummary.total.bAnteil / (auftragSummary.total.vkNetto / 100), 2)}
                            </td>
                            {auth.rolle !== 'AD' && (
                                <>
                                    <td className={classnames(percentClassName, styles.fAnteil)} />
                                    <td className={classnames(moneyClassName, styles.fAnteil)}>
                                        {formatPercent(
                                            auftragSummary.total.fAnteil / (auftragSummary.total.vkNetto / 100),
                                            2,
                                        )}
                                    </td>
                                </>
                            )}
                        </>
                    ) : (
                        <>
                            <td className={classnames(percentClassName, styles.bMarge)} />
                            <td className={classnames(percentClassName, styles.bMarge, styles.borderRight)} />
                            <td className={classnames(percentClassName, styles.tippProv)} />
                            <td className={classnames(percentClassName, styles.tippProv)} />
                            <td className={classnames(percentClassName, styles.skonto)} />
                            <td className={classnames(percentClassName, styles.skonto)} />
                            <td className={classnames(percentClassName, styles.fracht)} />
                            <td className={classnames(percentClassName, styles.fracht)} />
                            <td className={classnames(percentClassName, styles.nMarge)} />
                            <td className={classnames(percentClassName, styles.nMarge, styles.borderRight)} />
                            <td className={classnames(percentClassName, styles.bAnteil)} />
                            <td className={classnames(percentClassName, styles.bAnteil)} />
                            {auth.rolle !== 'AD' && (
                                <>
                                    <td className={classnames(percentClassName, styles.fAnteil)} />
                                    <td className={classnames(percentClassName, styles.fAnteil)} />
                                </>
                            )}
                        </>
                    )}
                </tr>
                <tr>
                    <td className={styles.tdBody}>Betreuer ({auftragSummary.total.betrUmCount})</td>
                    <td className={classnames(percentClassName, styles.ekNetto)} />
                    <td className={classnames(percentClassName, styles.ekNetto)} />
                    {renderMoneyWithGrowth(
                        auftragSummary.total.betrUm,
                        auftragSummaryCompare?.total.betrUm,
                        auftragSummaryAvg.total.betrUm,
                        styles.vkNetto,
                    )}
                    <td className={classnames(percentClassName, styles.bMarge)} />
                    <td className={classnames(percentClassName, styles.bMarge, styles.borderRight)}>
                        {auftragSummary.total.vkNetto
                            ? formatPercent(auftragSummary.total.betrUm / (auftragSummary.total.vkNetto / 100), 2)
                            : ''}
                    </td>
                    <td className={classnames(percentClassName, styles.tippProv)} />
                    <td className={classnames(percentClassName, styles.tippProv)} />
                    <td className={classnames(percentClassName, styles.skonto)} />
                    <td className={classnames(percentClassName, styles.skonto)} />
                    <td className={classnames(percentClassName, styles.fracht)} />
                    <td className={classnames(percentClassName, styles.fracht)} />
                    <td className={classnames(percentClassName, styles.nMarge)} />
                    <td className={classnames(percentClassName, styles.nMarge, styles.borderRight)} />
                    <td className={classnames(percentClassName, styles.bAnteil)} />
                    <td className={classnames(percentClassName, styles.bAnteil)} />
                    {auth.rolle !== 'AD' && (
                        <>
                            <td className={classnames(percentClassName, styles.fAnteil)} />
                            <td className={classnames(percentClassName, styles.fAnteil)} />
                        </>
                    )}
                </tr>

                <tr>
                    <td className={styles.tdBody}>
                        <strong>Gesamt</strong>
                    </td>
                    <td className={classnames(percentClassName, styles.ekNetto)} />
                    <td className={classnames(percentClassName, styles.ekNetto)} />
                    <td className={classnames(percentClassName, styles.vkNetto)} />
                    <td className={classnames(percentClassName, styles.vkNetto)} />
                    <td className={classnames(percentClassName, styles.bMarge)} />
                    <td className={classnames(percentClassName, styles.bMarge, styles.borderRight)} />
                    <td className={classnames(percentClassName, styles.tippProv)} />
                    <td className={classnames(percentClassName, styles.tippProv)} />
                    <td className={classnames(percentClassName, styles.skonto)} />
                    <td className={classnames(percentClassName, styles.skonto)} />
                    <td className={classnames(percentClassName, styles.fracht)} />
                    <td className={classnames(percentClassName, styles.fracht)} />
                    <td className={classnames(percentClassName, styles.nMarge)} />
                    <td className={classnames(percentClassName, styles.nMarge, styles.borderRight)} />
                    <td className={classnames(percentClassName, styles.bAnteil)} />
                    <td className={classnames(percentClassName, styles.bAnteil)} />
                    {auth.rolle !== 'AD' &&
                        renderMoneyWithGrowth(
                            auftragSummary.fAnteilTotal,
                            auftragSummaryCompare?.fAnteilTotal,
                            auftragSummaryAvg?.fAnteilTotal,
                            styles.fAnteilTotal,
                        )}
                </tr>

                <tr>
                    <td colSpan={19} className={classnames(styles.borderBottom, styles.noPadding)} />
                </tr>

                {showBetreuer && (
                    <>
                        <tr>
                            <td className={styles.tdBody}>
                                <strong>Betreuer</strong>
                            </td>
                            <td className={classnames(percentClassName, styles.ekNetto)} />
                            <td className={classnames(percentClassName, styles.ekNetto)} />
                            <td className={classnames(percentClassName, styles.vkNetto)} />
                            <td className={classnames(percentClassName, styles.vkNetto)} />
                            <td className={classnames(percentClassName, styles.bMarge)} />
                            <td className={classnames(percentClassName, styles.bMarge, styles.borderRight)} />
                            <td className={classnames(percentClassName, styles.tippProv)} />
                            <td className={classnames(percentClassName, styles.tippProv)} />
                            <td className={classnames(percentClassName, styles.skonto)} />
                            <td className={classnames(percentClassName, styles.skonto)} />
                            <td className={classnames(percentClassName, styles.fracht)} />
                            <td className={classnames(percentClassName, styles.fracht)} />
                            <td className={classnames(percentClassName, styles.nMarge)} />
                            <td className={classnames(percentClassName, styles.nMarge, styles.borderRight)} />
                            <td className={classnames(percentClassName, styles.bAnteil)} />
                            <td className={classnames(percentClassName, styles.bAnteil)} />
                            {auth.rolle !== 'AD' && (
                                <>
                                    <td className={classnames(percentClassName, styles.fAnteil)} />
                                    <td className={classnames(percentClassName, styles.fAnteil)} />
                                </>
                            )}
                        </tr>

                        {Object.values(auftragSummary.betreuerTotal)
                            .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                            .map((betreuerTotal) => {
                                return renderLine(
                                    (betreuerTotal.name || '?') + ' (' + betreuerTotal.count + ')',
                                    betreuerTotal,
                                    auftragSummaryCompare?.betreuerTotal[betreuerTotal.betreuerNr],
                                    auftragSummaryAvg?.betreuerTotal[betreuerTotal.betreuerNr],
                                );
                            })}
                        <tr>
                            <td colSpan={19} className={classnames(styles.borderBottom, styles.noPadding)} />
                        </tr>
                    </>
                )}

                {showTelBetreuer && (
                    <>
                        <tr>
                            <td className={styles.tdBody}>
                                <strong>Telefon-Betreuer</strong>
                            </td>
                            <td className={classnames(percentClassName, styles.ekNetto)} />
                            <td className={classnames(percentClassName, styles.ekNetto)} />
                            <td className={classnames(percentClassName, styles.vkNetto)} />
                            <td className={classnames(percentClassName, styles.vkNetto)} />
                            <td className={classnames(percentClassName, styles.bMarge)} />
                            <td className={classnames(percentClassName, styles.bMarge, styles.borderRight)} />
                            <td className={classnames(percentClassName, styles.tippProv)} />
                            <td className={classnames(percentClassName, styles.tippProv)} />
                            <td className={classnames(percentClassName, styles.skonto)} />
                            <td className={classnames(percentClassName, styles.skonto)} />
                            <td className={classnames(percentClassName, styles.fracht)} />
                            <td className={classnames(percentClassName, styles.fracht)} />
                            <td className={classnames(percentClassName, styles.nMarge)} />
                            <td className={classnames(percentClassName, styles.nMarge, styles.borderRight)} />
                            <td className={classnames(percentClassName, styles.bAnteil)} />
                            <td className={classnames(percentClassName, styles.bAnteil)} />
                            {auth.rolle !== 'AD' && (
                                <>
                                    <td className={classnames(percentClassName, styles.fAnteil)} />
                                    <td className={classnames(percentClassName, styles.fAnteil)} />
                                </>
                            )}
                        </tr>

                        {Object.values(auftragSummary.telBetreuerTotal)
                            .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                            .map((betreuerTotal) => {
                                return renderLine(
                                    (betreuerTotal.name || '?') + ' (' + betreuerTotal.count + ')',
                                    betreuerTotal,
                                    auftragSummaryCompare?.telBetreuerTotal[betreuerTotal.betreuerNr],
                                    auftragSummaryAvg?.telBetreuerTotal[betreuerTotal.betreuerNr],
                                );
                            })}
                        <tr>
                            <td colSpan={19} className={classnames(styles.strongBorderBottom, styles.noPadding)} />
                        </tr>
                    </>
                )}
            </>
        );
    };

    if (!lookupsLoadingFinished) {
        return <div />;
    }

    return (
        <div className={printView ? styles.printContainer : styles.container}>
            {(auftragSummaryJ0.loading ||
                auftragSummaryJ1.loading ||
                auftragSummaryJ2.loading ||
                auftragSummaryJ3.loading) && <LoadingIndicator />}
            {printView && (
                <div
                    dangerouslySetInnerHTML={{
                        __html:
                            // eslint-disable-next-line max-len
                            '<style>html, body, #root { overflow: unset !important; height: unset !important; }</style>',
                    }}
                />
            )}
            {!printView && (
                <table className={formStyles.reportFixedHeaderNoBgColor}>
                    <thead>{renderHeaderRow()}</thead>
                </table>
            )}
            <h1>Auftragsübersicht</h1>
            {printView && <FilterInfo filterValues={filterValues} />}
            <table className={formStyles.reportTable}>
                <thead>{renderHeaderRow()}</thead>
                <tbody>
                    {renderYear('', auftragSummaryPropsJ0, auftragSummaryJ0, auftragSummaryJ1, 0)}
                    {previousYear1 && renderYear('', auftragSummaryPropsJ1, auftragSummaryJ1, auftragSummaryJ2, 1)}
                    {previousYear2 && renderYear('', auftragSummaryPropsJ2, auftragSummaryJ2, auftragSummaryJ3, 2)}
                    {previousYear3 && renderYear('', auftragSummaryPropsJ3, auftragSummaryJ3, null, 2)}
                    {renderYear(
                        `Durchschnitt ${moment(auftragSummaryPropsJ1.datumVon).format('YYYY')}, ${moment(
                            auftragSummaryPropsJ2.datumVon,
                        ).format('YYYY')}, ${moment(auftragSummaryPropsJ3.datumVon).format('YYYY')}`,
                        null,
                        auftragSummaryAvg,
                        null,
                        0,
                    )}
                </tbody>
            </table>
            <div className={styles.produktgruppen}>
                Produktgruppen:{' '}
                {Object.keys(auftragSummaryAvg?.usedProduktgruppen || {})
                    .map(
                        (produktGruppeNr) =>
                            produktgruppenByNr[produktGruppeNr]?.text +
                            ' (' +
                            auftragSummaryAvg?.usedProduktgruppen?.[produktGruppeNr] +
                            ')',
                    )
                    .join(', ')}
            </div>
        </div>
    );
};
